import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, map, Observable, of, throwError } from 'rxjs';

import { Banner, User, UserConfiguration, UserInfo, UserPasswordChange } from '../models/user.model';
import { LOCAL_STORAGE } from 'app/tokens/browser.tokens';
import { parseApiDecimal } from '../utils/api-decimal.utils';

type ApiUser = Omit<User, 'allowedPPF' | 'allowedWindows'> & {
  allowedPPF: string;
  allowedWindows: string;
};

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly localStorage = inject(LOCAL_STORAGE);

  getUser(userId: number): Observable<User> {
    return this.http.get<{ data: ApiUser; statusCode: number }>('/api/user/' + userId).pipe(map(response => this.normalizeUser(response.data)));
  }

  updateUser(userData: UserInfo): Observable<User> {
    return this.http.put<{ data: ApiUser; statusCode: number }>(`/api/user`, userData).pipe(map(response => this.normalizeUser(response.data)));
  }

  changePassword(passwords: UserPasswordChange): Observable<void> {
    return this.http.put<void>(`/api/user/set-password`, passwords).pipe(map(() => undefined));
  }

  getUserConfigurationFromStorage(): UserConfiguration {
    return this.readStorageValue<UserConfiguration>('configuration') ?? {};
  }

  setUserConfigurationToStorage(config: UserConfiguration): void {
    this.localStorage?.setItem('configuration', JSON.stringify(config));
  }

  getBanner(): Observable<Banner | null> {
    return this.http.get<{ data: Banner; statusCode: number }>('/api/banner').pipe(
      map(response => response.data),
      catchError(error => (isBannerNotFound(error) ? of(null) : throwError(() => error))),
    );
  }

  private normalizeUser(user: ApiUser): User {
    return {
      ...user,
      allowedPPF: parseApiDecimal(user.allowedPPF),
      allowedWindows: parseApiDecimal(user.allowedWindows),
    };
  }

  private readStorageValue<T>(key: string): T | null {
    const value = this.localStorage?.getItem(key);
    if (!value) {
      return null;
    }

    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  }
}

function isBannerNotFound(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const status =
    'statusCode' in error && typeof error.statusCode === 'number'
      ? error.statusCode
      : 'status' in error && typeof error.status === 'number'
        ? error.status
        : undefined;
  if (status !== 404 || !('error' in error)) {
    return false;
  }

  const body = error.error;

  return typeof body === 'object' && body !== null && 'error' in body && body.error === 'BANNER_NOT_FOUND';
}
